import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

export const env = createEnv({
  server: {
    SERVER_URL: z.string().url().optional(),
    ENVIRONMENT: z.enum(["development", "production"]).optional(),
    SENTRY_AUTH_TOKEN: z.string().optional(),
    SUPABASE_SECRET_KEY: z.string().min(1),
    SMTP_HOST: z.string().min(1),
    SMTP_PORT: z.coerce.number(),
    SMTP_USER: z.string().min(1),
    SMTP_PASS: z.string().min(1),
    MOLLIE_TOKENS_ENDPOINT: z.string().url(),
    MOLLIE_CLIENT_ID: z.string().min(1),
    MOLLIE_CLIENT_SECRET: z.string().min(1),
    MOLLIE_REDIRECT_URI: z.string().url(),
    MOLLIE_ONLINE_ORDERS_WEBHOOK_URL: z.string().url(),
    EXPO_ACCESS_TOKEN: z.string().min(1),
    POSTCODE_API_BASE_URL: z.string().url(),
    POSTCODE_API_KEY: z.string().min(1),
    POSTCODE_API_SECRET: z.string().min(1),
    // Vrodex Inbox: `inbox.vrodex.com`. Only used to build the Reply-To
    // address on order confirmation mail; receiving happens in apps/server.
    // Must match packages/api or a guest replying to a confirmation would be
    // sent to a host nothing is listening on. Unset keeps the merchant's
    // own address, which is what they had before the inbox.
    RESEND_INBOUND_DOMAIN: z.string().optional(),
    // The Vrodex-owned host every printed QR code points at
    // (order.vrodex.com/t/<token>). Requests on it are redirected to the
    // merchant's current ordering domain, so a domain change never invalidates
    // a sticker; a merchant without a domain is served on this host itself.
    // Must match VITE_QR_LINK_BASE_URL in apps/app. Locally, point
    // both at a second name for this dev server, e.g. 127.0.0.1:3030.
    QR_LINK_HOST: z.string().default("order.vrodex.com"),
  },

  /**
   * The prefix that client-side variables must have. This is enforced both at
   * a type-level and at runtime.
   */
  clientPrefix: "VITE_",

  client: {
    VITE_APP_TITLE: z.string().min(1).optional(),
    VITE_OPENREPLAY_PROJECT_KEY: z.string(),
    VITE_OPENREPLAY_INGEST_POINT: z.string().url(),
    VITE_SUPABASE_URL: z.string().url(),
    VITE_SUPABASE_PUBLISHABLE_KEY: z.string(),
    VITE_MAPBOX_ACCESS_TOKEN: z.string(),
    VITE_PUBLIC_POSTHOG_KEY: z.string(),
    VITE_PUBLIC_POSTHOG_HOST: z.string().url(),
    VITE_SENTRY_DSN: z.string(),
  },

  /**
   * What object holds the environment variables at runtime. Vite's
   * `import.meta.env` only exposes `VITE_`-prefixed (and a few built-in)
   * variables, so server-only vars must be read from `process.env`. We merge
   * both so validation works in every context: client bundles, the Nitro
   * SSR server, and the Node process that evaluates `vite.config.ts`.
   */
  runtimeEnv: {
    ...(typeof process !== "undefined" ? process.env : {}),
    ...import.meta.env,
  },

  /**
   * By default, this library will feed the environment variables directly to
   * the Zod validator.
   *
   * This means that if you have an empty string for a value that is supposed
   * to be a number (e.g. `PORT=` in a ".env" file), Zod will incorrectly flag
   * it as a type mismatch violation. Additionally, if you have an empty string
   * for a value that is supposed to be a string with a default value (e.g.
   * `DOMAIN=` in an ".env" file), the default value will never be applied.
   *
   * In order to solve these issues, we recommend that all new projects
   * explicitly specify this option as true.
   */
  emptyStringAsUndefined: true,
});
